/**
 * A hover/focus tooltip over rules text. It began as the pilot editor's skill-trigger and talent tip
 * (both retired for the unit sheet's rows, `decisions/0206`); the combat unit-action tips use it now.
 *
 * `<InfoTip>` wraps an arbitrary `trigger` node and reveals a brutalist panel on hover OR
 * keyboard focus (the wrapper is `tabIndex={0}`). The panel is portalled to `document.body`
 * with `position: fixed`, so the desktop window's scroll host (`overflow-y: auto`) never
 * clips it; placement flips above the trigger when it sits in the lower part of the
 * viewport, and the left edge is clamped into view. Styling lives in `pilot-info-tip.css`.
 */
import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import "./pilot-info-tip.css";

/**
 * The panel's width, in CSS pixels — **the single source of truth**, applied inline and also read by
 * `show()` to clamp the panel's left edge into the viewport. Deliberately NOT also stated in the
 * stylesheet: a second declaration is a silent-drift footgun, since the panel still renders when the
 * two disagree, just clipped or mis-clamped at the screen edge.
 *
 * 440 because every body this panel shows is multi-paragraph rules text (`RulesText`): it puts
 * the prose measure around 68 characters, inside the comfortable-reading band (at 320 it was ~45, so
 * a talent rank wrapped every six or seven words and its paragraph boundaries were indistinguishable
 * from the wraps). The `max-width` in the stylesheet keeps a narrow viewport from pushing it
 * off-screen.
 */
const TIP_W = 440;

export function InfoTip({
  className,
  trigger,
  onActivate,
  inline,
  open,
  children,
}: {
  /** Extra class(es) on the trigger wrapper. */
  className?: string;
  /** The always-visible content (the skill/talent label). */
  trigger: ReactNode;
  /** When set, the wrapper becomes a button — click or Enter/Space opens the record (e.g. in the COMPENDIUM). */
  onActivate?: () => void;
  /**
   * A trigger inside another control (the command menu's row button): render a non-focusable
   * `<span>` instead of the focusable `<div>`, and let a click on it do nothing rather than reach
   * the control. Keyboard users get the panel through `open` (the host control's focus).
   */
  inline?: boolean;
  /** Show the panel while true (the host control has keyboard focus), beside hover. */
  open?: boolean;
  /** The tooltip panel content (a `*TipBody`). */
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - TIP_W - 12));
    const flipUp = r.bottom > window.innerHeight * 0.6;
    setPos(flipUp ? { left, bottom: window.innerHeight - r.top + 6 } : { left, top: r.bottom + 6 });
  };
  const hide = () => setPos(null);
  useLayoutEffect(() => {
    if (open === undefined) return;
    if (open) show();
    else hide();
    // `show` reads the trigger's rect at the moment `open` flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const panel =
    pos &&
    createPortal(
      <div className="be-tip" role="tooltip" style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: TIP_W }}>
        {children}
      </div>,
      document.body,
    );
  if (inline) {
    return (
      <span
        ref={ref as RefObject<HTMLSpanElement>}
        className={`be-tiptrigger${className ? ` ${className}` : ""}`}
        onMouseEnter={show}
        onMouseLeave={() => {
          if (!open) hide();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {trigger}
        {panel}
      </span>
    );
  }

  return (
    <div
      ref={ref as RefObject<HTMLDivElement>}
      className={`be-tiptrigger${onActivate ? " be-tiptrigger-link" : ""}${className ? ` ${className}` : ""}`}
      tabIndex={0}
      role={onActivate ? "button" : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={onActivate}
      onKeyDown={
        onActivate
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onActivate();
              }
            }
          : undefined
      }
    >
      {trigger}
      {panel}
    </div>
  );
}
